
import { DotNetModule } from '../types';

export const DOTNET_INTERMEDIATE_MODULES: DotNetModule[] = [
  {
    id: 'dotnet-oop-fundamentals',
    title: 'Object-Oriented Programming Fundamentals',
    description: 'Learn the core principles of OOP in .NET including classes, objects, inheritance, and polymorphism.',
    content: `
# Object-Oriented Programming Fundamentals in .NET

## Introduction to OOP

Object-Oriented Programming (OOP) is a programming paradigm that organizes code into objects that contain both data (properties) and behavior (methods). .NET languages like C# are designed with OOP principles at their core.

## The Four Pillars of OOP

### 1. Encapsulation
Encapsulation is the bundling of data and methods that operate on that data within a single unit (class), while hiding the internal implementation details.

\`\`\`csharp
public class BankAccount
{
    private decimal balance; // Private field - encapsulated
    
    public decimal Balance // Public property - controlled access
    {
        get { return balance; }
        private set { balance = value; }
    }
    
    public void Deposit(decimal amount)
    {
        if (amount > 0)
            balance += amount;
    }
    
    public bool Withdraw(decimal amount)
    {
        if (amount > 0 && amount <= balance)
        {
            balance -= amount;
            return true;
        }
        return false;
    }
}
\`\`\`

### 2. Inheritance
Inheritance allows a class to inherit properties and methods from another class, promoting code reuse and establishing relationships between classes.

\`\`\`csharp
public class Vehicle // Base class
{
    public string Brand { get; set; }
    public int Year { get; set; }
    
    public virtual void Start()
    {
        Console.WriteLine("Vehicle is starting...");
    }
}

public class Car : Vehicle // Derived class
{
    public int NumberOfDoors { get; set; }
    
    public override void Start()
    {
        Console.WriteLine("Car engine is starting...");
    }
}
\`\`\`

### 3. Polymorphism
Polymorphism allows objects of different types to be treated as objects of a common base type, while still maintaining their specific behavior.

\`\`\`csharp
public abstract class Shape
{
    public abstract double CalculateArea();
}

public class Circle : Shape
{
    public double Radius { get; set; }
    
    public override double CalculateArea()
    {
        return Math.PI * Radius * Radius;
    }
}

public class Rectangle : Shape
{
    public double Width { get; set; }
    public double Height { get; set; }
    
    public override double CalculateArea()
    {
        return Width * Height;
    }
}
\`\`\`

### 4. Abstraction
Abstraction hides complex implementation details and shows only the necessary features of an object.

\`\`\`csharp
public interface IDataRepository
{
    void Save(object data);
    object Load(int id);
}

public class DatabaseRepository : IDataRepository
{
    public void Save(object data)
    {
        // Complex database save logic hidden
        Console.WriteLine("Saving to database...");
    }
    
    public object Load(int id)
    {
        // Complex database load logic hidden
        Console.WriteLine("Loading from database...");
        return new object();
    }
}
\`\`\`

## Classes and Objects

### Defining Classes
\`\`\`csharp
public class Student
{
    // Fields
    private string name;
    private int age;
    
    // Properties
    public string Name 
    { 
        get { return name; }
        set { name = value; }
    }
    
    public int Age
    {
        get { return age; }
        set 
        { 
            if (value >= 0)
                age = value;
        }
    }
    
    // Constructor
    public Student(string name, int age)
    {
        Name = name;
        Age = age;
    }
    
    // Methods
    public void Study()
    {
        Console.WriteLine($"{Name} is studying.");
    }
}
\`\`\`

### Creating Objects
\`\`\`csharp
// Creating instances
Student student1 = new Student("Alice", 20);
Student student2 = new Student("Bob", 22);

// Using objects
student1.Study();
Console.WriteLine($"Student: {student1.Name}, Age: {student1.Age}");
\`\`\`
    `,
    duration: '45 minutes',
    difficulty: 'intermediate',
    objectives: [
      'Understand the four pillars of OOP',
      'Create classes with proper encapsulation',
      'Implement inheritance hierarchies',
      'Apply polymorphism in .NET applications',
      'Use abstraction through interfaces and abstract classes'
    ],
    order: 1,
    quiz: {
      questions: [
        {
          id: 'oop-q1',
          question: 'Which OOP principle is demonstrated by using private fields with public properties?',
          options: [
            'Inheritance',
            'Polymorphism',
            'Encapsulation',
            'Abstraction'
          ],
          correctAnswer: 2,
          explanation: 'Encapsulation is the principle of bundling data and methods together while hiding internal implementation details through access modifiers.'
        },
        {
          id: 'oop-q2',
          question: 'What keyword is used in C# to create a class that inherits from another class?',
          options: [
            'extends',
            'inherits',
            ':',
            'base'
          ],
          correctAnswer: 2,
          explanation: 'In C#, the colon (:) is used to indicate inheritance, e.g., "class Car : Vehicle".'
        },
        {
          id: 'oop-q3',
          question: 'Which keyword allows a method in a derived class to provide a different implementation than the base class?',
          options: [
            'virtual',
            'override',
            'abstract',
            'new'
          ],
          correctAnswer: 1,
          explanation: 'The override keyword is used in derived classes to provide a new implementation of a virtual or abstract method from the base class.'
        },
        {
          id: 'oop-q4',
          question: 'What is the main benefit of using interfaces in .NET?',
          options: [
            'They provide implementation details',
            'They enable multiple inheritance of behavior',
            'They store data',
            'They improve performance'
          ],
          correctAnswer: 1,
          explanation: 'Interfaces enable multiple inheritance of behavior in .NET, allowing a class to implement multiple interfaces while inheriting from only one base class.'
        },
        {
          id: 'oop-q5',
          question: 'Which access modifier makes a member accessible only within the same class?',
          options: [
            'public',
            'protected',
            'internal',
            'private'
          ],
          correctAnswer: 3,
          explanation: 'The private access modifier restricts access to the member only within the same class where it is declared.'
        }
      ]
    }
  },
  {
    id: 'dotnet-advanced-csharp',
    title: 'Advanced C# Features',
    description: 'Explore advanced C# language features including LINQ, delegates, events, and async programming.',
    content: `
# Advanced C# Features

## LINQ (Language Integrated Query)

LINQ provides a powerful way to query data from various sources using a consistent syntax.

### Query Syntax
\`\`\`csharp
var numbers = new[] { 1, 2, 3, 4, 5, 6, 7, 8, 9, 10 };

var evenNumbers = from n in numbers
                  where n % 2 == 0
                  select n;

foreach (var num in evenNumbers)
{
    Console.WriteLine(num); // Output: 2, 4, 6, 8, 10
}
\`\`\`

### Method Syntax
\`\`\`csharp
var evenNumbers = numbers.Where(n => n % 2 == 0);
var squares = numbers.Select(n => n * n);
var sum = numbers.Sum();
var average = numbers.Average();
\`\`\`

## Delegates and Events

### Delegates
Delegates represent references to methods with a specific signature.

\`\`\`csharp
// Declare delegate type
public delegate void ProcessHandler(string message);

// Method that matches the delegate signature
public static void ProcessMessage(string message)
{
    Console.WriteLine($"Processing: {message}");
}

// Using delegates
ProcessHandler handler = ProcessMessage;
handler("Hello World");

// Multicast delegates
ProcessHandler multiHandler = ProcessMessage;
multiHandler += msg => Console.WriteLine($"Log: {msg}");
multiHandler("Test"); // Calls both methods
\`\`\`

### Events
Events provide a way for a class to notify other classes when something happens.

\`\`\`csharp
public class Publisher
{
    // Declare event
    public event Action<string> SomethingHappened;
    
    protected virtual void OnSomethingHappened(string message)
    {
        SomethingHappened?.Invoke(message);
    }
    
    public void DoSomething()
    {
        // Some work...
        OnSomethingHappened("Work completed!");
    }
}

public class Subscriber
{
    public void Subscribe(Publisher pub)
    {
        pub.SomethingHappened += HandleEvent;
    }
    
    private void HandleEvent(string message)
    {
        Console.WriteLine($"Received: {message}");
    }
}
\`\`\`

## Async/Await Programming

### Basic Async Method
\`\`\`csharp
public async Task<string> GetDataAsync()
{
    using (var client = new HttpClient())
    {
        var response = await client.GetStringAsync("https://api.example.com/data");
        return response;
    }
}

// Calling async method
public async Task ProcessDataAsync()
{
    try
    {
        var data = await GetDataAsync();
        Console.WriteLine(data);
    }
    catch (Exception ex)
    {
        Console.WriteLine($"Error: {ex.Message}");
    }
}
\`\`\`

### Task.Run for CPU-bound operations
\`\`\`csharp
public async Task<int> CalculateAsync(int n)
{
    return await Task.Run(() =>
    {
        // Simulate CPU-intensive work
        Thread.Sleep(1000);
        return n * n;
    });
}
\`\`\`

## Generics

### Generic Classes
\`\`\`csharp
public class Repository<T> where T : class
{
    private List<T> items = new List<T>();
    
    public void Add(T item)
    {
        items.Add(item);
    }
    
    public T GetById(int id)
    {
        return items.ElementAtOrDefault(id);
    }
    
    public IEnumerable<T> GetAll()
    {
        return items;
    }
}
\`\`\`

### Generic Methods
\`\`\`csharp
public static T FindMax<T>(T[] array) where T : IComparable<T>
{
    if (array.Length == 0)
        throw new ArgumentException("Array cannot be empty");
    
    T max = array[0];
    for (int i = 1; i < array.Length; i++)
    {
        if (array[i].CompareTo(max) > 0)
            max = array[i];
    }
    return max;
}

// Usage
int[] numbers = { 3, 7, 2, 9, 1 };
int maxNumber = FindMax(numbers); // Returns 9
\`\`\`

## Extension Methods

Extension methods allow you to add new methods to existing types without modifying them.

\`\`\`csharp
public static class StringExtensions
{
    public static bool IsValidEmail(this string email)
    {
        try
        {
            var addr = new System.Net.Mail.MailAddress(email);
            return addr.Address == email;
        }
        catch
        {
            return false;
        }
    }
    
    public static string ToTitleCase(this string input)
    {
        return System.Globalization.CultureInfo.CurrentCulture.TextInfo.ToTitleCase(input);
    }
}

// Usage
string email = "user@example.com";
bool isValid = email.IsValidEmail(); // Extension method call

string name = "john doe";
string titleCase = name.ToTitleCase(); // "John Doe"
\`\`\`
    `,
    duration: '60 minutes',
    difficulty: 'advanced',
    objectives: [
      'Master LINQ for data querying',
      'Implement delegates and events',
      'Write asynchronous code with async/await',
      'Create and use generic types',
      'Develop extension methods'
    ],
    order: 2,
    quiz: {
      questions: [
        {
          id: 'advanced-q1',
          question: 'What is the primary purpose of LINQ in C#?',
          options: [
            'To provide object-oriented programming features',
            'To enable querying data from various sources with consistent syntax',
            'To handle exceptions',
            'To manage memory allocation'
          ],
          correctAnswer: 1,
          explanation: 'LINQ (Language Integrated Query) provides a consistent syntax for querying data from various sources like collections, databases, and XML.'
        },
        {
          id: 'advanced-q2',
          question: 'Which keyword is used to wait for an asynchronous operation to complete?',
          options: [
            'async',
            'await',
            'wait',
            'delay'
          ],
          correctAnswer: 1,
          explanation: 'The await keyword is used to asynchronously wait for a Task to complete without blocking the current thread.'
        },
        {
          id: 'advanced-q3',
          question: 'What is a delegate in C#?',
          options: [
            'A type that represents references to methods with a specific signature',
            'A class that inherits from another class',
            'A method that returns void',
            'An interface implementation'
          ],
          correctAnswer: 0,
          explanation: 'A delegate is a type that represents references to methods with a specific signature, enabling method parameter passing and callback functionality.'
        },
        {
          id: 'advanced-q4',
          question: 'What constraint is commonly used with generic types to ensure the type parameter is a reference type?',
          options: [
            'where T : struct',
            'where T : class',
            'where T : new()',
            'where T : IComparable'
          ],
          correctAnswer: 1,
          explanation: 'The "where T : class" constraint ensures that the generic type parameter T must be a reference type (class, interface, delegate, or array).'
        },
        {
          id: 'advanced-q5',
          question: 'How do you create an extension method in C#?',
          options: [
            'Use the virtual keyword',
            'Use the static keyword in a static class with this parameter',
            'Use the override keyword',
            'Use the abstract keyword'
          ],
          correctAnswer: 1,
          explanation: 'Extension methods must be defined as static methods in a static class, with the first parameter preceded by the "this" keyword.'
        }
      ]
    }
  },
  {
    id: 'dotnet-web-development',
    title: 'Web Development with ASP.NET Core',
    description: 'Build modern web applications using ASP.NET Core, including MVC, Web API, and Entity Framework.',
    content: `
# Web Development with ASP.NET Core

## Introduction to ASP.NET Core

ASP.NET Core is a cross-platform, high-performance framework for building modern, cloud-based, Internet-connected applications.

### Key Features
- Cross-platform (Windows, macOS, Linux)
- High performance
- Built-in dependency injection
- Modular architecture
- Cloud-ready

## MVC Pattern

### Model-View-Controller Architecture
\`\`\`csharp
// Model
public class Product
{
    public int Id { get; set; }
    public string Name { get; set; }
    public decimal Price { get; set; }
    public string Description { get; set; }
}

// Controller
public class ProductController : Controller
{
    private readonly IProductService _productService;
    
    public ProductController(IProductService productService)
    {
        _productService = productService;
    }
    
    public async Task<IActionResult> Index()
    {
        var products = await _productService.GetAllProductsAsync();
        return View(products);
    }
    
    public async Task<IActionResult> Details(int id)
    {
        var product = await _productService.GetProductByIdAsync(id);
        if (product == null)
            return NotFound();
        
        return View(product);
    }
    
    [HttpPost]
    public async Task<IActionResult> Create(Product product)
    {
        if (ModelState.IsValid)
        {
            await _productService.CreateProductAsync(product);
            return RedirectToAction(nameof(Index));
        }
        return View(product);
    }
}
\`\`\`

## Web API Development

### Creating REST APIs
\`\`\`csharp
[ApiController]
[Route("api/[controller]")]
public class ProductsController : ControllerBase
{
    private readonly IProductService _productService;
    
    public ProductsController(IProductService productService)
    {
        _productService = productService;
    }
    
    [HttpGet]
    public async Task<ActionResult<IEnumerable<Product>>> GetProducts()
    {
        var products = await _productService.GetAllProductsAsync();
        return Ok(products);
    }
    
    [HttpGet("{id}")]
    public async Task<ActionResult<Product>> GetProduct(int id)
    {
        var product = await _productService.GetProductByIdAsync(id);
        if (product == null)
            return NotFound();
        
        return Ok(product);
    }
    
    [HttpPost]
    public async Task<ActionResult<Product>> PostProduct(Product product)
    {
        await _productService.CreateProductAsync(product);
        return CreatedAtAction(nameof(GetProduct), new { id = product.Id }, product);
    }
    
    [HttpPut("{id}")]
    public async Task<IActionResult> PutProduct(int id, Product product)
    {
        if (id != product.Id)
            return BadRequest();
        
        await _productService.UpdateProductAsync(product);
        return NoContent();
    }
    
    [HttpDelete("{id}")]
    public async Task<IActionResult> DeleteProduct(int id)
    {
        await _productService.DeleteProductAsync(id);
        return NoContent();
    }
}
\`\`\`

## Entity Framework Core

### DbContext Setup
\`\`\`csharp
public class ApplicationDbContext : DbContext
{
    public ApplicationDbContext(DbContextOptions<ApplicationDbContext> options)
        : base(options)
    {
    }
    
    public DbSet<Product> Products { get; set; }
    public DbSet<Category> Categories { get; set; }
    
    protected override void OnModelCreating(ModelBuilder modelBuilder)
    {
        modelBuilder.Entity<Product>(entity =>
        {
            entity.HasKey(e => e.Id);
            entity.Property(e => e.Name).IsRequired().HasMaxLength(100);
            entity.Property(e => e.Price).HasColumnType("decimal(18,2)");
        });
        
        base.OnModelCreating(modelBuilder);
    }
}
\`\`\`

### Repository Pattern
\`\`\`csharp
public interface IProductRepository
{
    Task<IEnumerable<Product>> GetAllAsync();
    Task<Product> GetByIdAsync(int id);
    Task<Product> AddAsync(Product product);
    Task UpdateAsync(Product product);
    Task DeleteAsync(int id);
}

public class ProductRepository : IProductRepository
{
    private readonly ApplicationDbContext _context;
    
    public ProductRepository(ApplicationDbContext context)
    {
        _context = context;
    }
    
    public async Task<IEnumerable<Product>> GetAllAsync()
    {
        return await _context.Products.ToListAsync();
    }
    
    public async Task<Product> GetByIdAsync(int id)
    {
        return await _context.Products.FindAsync(id);
    }
    
    public async Task<Product> AddAsync(Product product)
    {
        _context.Products.Add(product);
        await _context.SaveChangesAsync();
        return product;
    }
    
    public async Task UpdateAsync(Product product)
    {
        _context.Entry(product).State = EntityState.Modified;
        await _context.SaveChangesAsync();
    }
    
    public async Task DeleteAsync(int id)
    {
        var product = await _context.Products.FindAsync(id);
        if (product != null)
        {
            _context.Products.Remove(product);
            await _context.SaveChangesAsync();
        }
    }
}
\`\`\`

## Dependency Injection

### Service Registration
\`\`\`csharp
// Program.cs (ASP.NET Core 6+)
var builder = WebApplication.CreateBuilder(args);

// Add services to the container
builder.Services.AddControllers();
builder.Services.AddDbContext<ApplicationDbContext>(options =>
    options.UseSqlServer(builder.Configuration.GetConnectionString("DefaultConnection")));

// Register custom services
builder.Services.AddScoped<IProductRepository, ProductRepository>();
builder.Services.AddScoped<IProductService, ProductService>();

var app = builder.Build();

// Configure the HTTP request pipeline
if (app.Environment.IsDevelopment())
{
    app.UseDeveloperExceptionPage();
}

app.UseRouting();
app.UseAuthorization();
app.MapControllers();

app.Run();
\`\`\`

## Middleware

### Custom Middleware
\`\`\`csharp
public class RequestLoggingMiddleware
{
    private readonly RequestDelegate _next;
    private readonly ILogger<RequestLoggingMiddleware> _logger;
    
    public RequestLoggingMiddleware(RequestDelegate next, ILogger<RequestLoggingMiddleware> logger)
    {
        _next = next;
        _logger = logger;
    }
    
    public async Task InvokeAsync(HttpContext context)
    {
        var stopwatch = Stopwatch.StartNew();
        
        _logger.LogInformation("Request: {Method} {Path}", 
            context.Request.Method, context.Request.Path);
        
        await _next(context);
        
        stopwatch.Stop();
        _logger.LogInformation("Response: {StatusCode} in {ElapsedMs}ms", 
            context.Response.StatusCode, stopwatch.ElapsedMilliseconds);
    }
}

// Extension method for easy registration
public static class MiddlewareExtensions
{
    public static IApplicationBuilder UseRequestLogging(this IApplicationBuilder builder)
    {
        return builder.UseMiddleware<RequestLoggingMiddleware>();
    }
}

// Usage in Program.cs
app.UseRequestLogging();
\`\`\`
    `,
    duration: '75 minutes',
    difficulty: 'advanced',
    objectives: [
      'Understand ASP.NET Core architecture',
      'Implement MVC pattern in web applications',
      'Create RESTful Web APIs',
      'Use Entity Framework Core for data access',
      'Apply dependency injection and middleware'
    ],
    order: 3,
    quiz: {
      questions: [
        {
          id: 'web-q1',
          question: 'What does MVC stand for in ASP.NET Core?',
          options: [
            'Model-View-Component',
            'Model-View-Controller',
            'Module-View-Controller',
            'Model-Virtual-Controller'
          ],
          correctAnswer: 1,
          explanation: 'MVC stands for Model-View-Controller, which is an architectural pattern that separates application logic into three main components.'
        },
        {
          id: 'web-q2',
          question: 'Which attribute is used to mark a class as a Web API controller?',
          options: [
            '[Controller]',
            '[WebApi]',
            '[ApiController]',
            '[RestController]'
          ],
          correctAnswer: 2,
          explanation: 'The [ApiController] attribute is used to mark a class as a Web API controller, enabling API-specific behaviors like automatic model validation.'
        },
        {
          id: 'web-q3',
          question: 'What is the primary purpose of Entity Framework Core?',
          options: [
            'To create user interfaces',
            'To provide Object-Relational Mapping (ORM) capabilities',
            'To handle HTTP requests',
            'To manage application configuration'
          ],
          correctAnswer: 1,
          explanation: 'Entity Framework Core is an ORM that enables developers to work with databases using .NET objects, eliminating the need for most data-access code.'
        },
        {
          id: 'web-q4',
          question: 'In ASP.NET Core, where is dependency injection configured?',
          options: [
            'In the controller constructor',
            'In the Program.cs file during service registration',
            'In the appsettings.json file',
            'In the middleware pipeline'
          ],
          correctAnswer: 1,
          explanation: 'Dependency injection is configured in Program.cs (or Startup.cs in older versions) during the service registration phase using the Services property.'
        },
        {
          id: 'web-q5',
          question: 'What is middleware in ASP.NET Core?',
          options: [
            'A database access layer',
            'Components that handle HTTP requests and responses in a pipeline',
            'A user interface framework',
            'A logging mechanism'
          ],
          correctAnswer: 1,
          explanation: 'Middleware are components that form a pipeline to handle HTTP requests and responses, with each component choosing whether to pass the request to the next component.'
        }
      ]
    }
  }
];
